import { useMemo } from 'react';

import {
    ProductPriceSortingModes,
    ProductSourcingModes,
} from 'entities/ProductSourcing/model/types/ProductSourcingSchema';
import { getProposalQuantities } from 'entities/Proposal/lib/getProposalQuantities';
import {
    HydratedProposal,
    Proposal,
    SelectedProposal,
} from 'entities/Proposal/model/types/ProposalSchema';
import { Vendor } from 'entities/Vendors';

interface UseSortedProposalsProps {
    proposals: Proposal[] | undefined;
    vendorsMap: Record<string, Vendor> | undefined;
    selectedProposals: SelectedProposal[] | undefined;
    selectedOrderProperty: string | undefined;
    selectedPropertyId: string | undefined;
    sourcingSettings:
        | {
              mode?: string;
              sortingMode?: string;
              preferredSuppliers?: Array<{ uid: string }>;
          }
        | undefined;
    isPreSubmission: boolean;
}

const sortByPrice = (proposals: HydratedProposal[]): HydratedProposal[] => {
    return proposals.sort((a, b) => {
        const isVendorAActive = a.hydratedVendor?.isOperationsActivated ?? true;
        const isVendorBActive = b.hydratedVendor?.isOperationsActivated ?? true;
        const priceA = isVendorAActive ? a.sellPrice || 0 : -1;
        const priceB = isVendorBActive ? b.sellPrice || 0 : -1;

        // Primary sort: active vendors first, then inactive vendors
        if (isVendorAActive !== isVendorBActive) {
            return isVendorAActive ? -1 : 1;
        }

        // Secondary sort by price (only within active or inactive groups)
        if (priceA !== priceB) {
            return priceA - priceB;
        }

        // Tertiary sort by vendor name alphabetically when prices are equal
        const nameA = a.seller.name || '';
        const nameB = b.seller.name || '';
        return nameA.localeCompare(nameB);
    });
};

const linkSelectedProposals = (
    proposals: Proposal[],
    vendorsMap: Record<string, Vendor> | undefined,
    selectedProposals: SelectedProposal[] | undefined,
    isPreSubmission: boolean,
): HydratedProposal[] => {
    // If this is after an order has been places, we don't have proposals
    // So we simulate the return to render the correct table
    if (!isPreSubmission) {
        return getPostSubmissionProposals(vendorsMap, selectedProposals);
    }

    return proposals.map((proposal) => ({
        ...proposal,
        hydratedVendor: vendorsMap?.[proposal.seller.uid],
        selectedProposal:
            selectedProposals?.find(
                (selectedProposal) => selectedProposal.uid === proposal.uid,
            ) || null,
    }));
};

const getPostSubmissionProposals = (
    vendorsMap: Record<string, Vendor> | undefined,
    selectedProposals: SelectedProposal[] | undefined,
): HydratedProposal[] => {
    if (!selectedProposals) {
        return [];
    }

    return selectedProposals
        .filter((selectedProposal) => {
            const { selectedQty, approvedQty } =
                getProposalQuantities(selectedProposal);
            return selectedQty > 0 || approvedQty > 0;
        })
        .map((selectedProposal) => {
            // Provide default values for required Proposal properties
            // We don't need this post submission, but it's required for the HydratedProposal type
            const vendor = vendorsMap?.[selectedProposal.sellerUid];
            const { approvedQty } = getProposalQuantities(selectedProposal);
            const hydratedProposal: HydratedProposal = {
                condition: '',
                externalInvId: '',
                invId: '',
                isActive: true,
                isUnlimitedQuantity: false,
                prices: {
                    discount: 0,
                    freight: 0,
                    subtotal: 0,
                    total: 0,
                    tax: 0,
                    price: 0,
                },
                quantity: approvedQty,
                seller: {
                    name: vendor?.title || '',
                    paId: '',
                    parId: '',
                    uid: vendor?.externalUuid || '',
                },
                uid: selectedProposal.uid,
                units: '',
                isOutOfStock: false,
                productSourcingMode: '',
                hydratedVendor: vendor,
                selectedProposal: selectedProposal,
            };

            return hydratedProposal;
        });
};

export const useSortedProposals = ({
    proposals,
    vendorsMap,
    selectedProposals,
    selectedOrderProperty,
    selectedPropertyId,
    sourcingSettings,
    isPreSubmission,
}: UseSortedProposalsProps): HydratedProposal[] => {
    return useMemo(() => {
        const proposalList = proposals || [];
        const linkedProposals = linkSelectedProposals(
            proposalList,
            vendorsMap,
            selectedProposals,
            isPreSubmission,
        );

        // Return early if properties don't match (handles hard refresh delay)
        if (selectedOrderProperty !== selectedPropertyId) {
            return linkedProposals;
        }

        const mode =
            sourcingSettings?.mode || ProductSourcingModes.AvailabilityPrice;
        const sortingMode =
            sourcingSettings?.sortingMode || ProductPriceSortingModes.BestPrice;

        // If it's availability price mode, sort by seller name
        if (mode === ProductSourcingModes.AvailabilityPrice) {
            return sortByPrice(linkedProposals);
        }

        // Group preferred suppliers first
        const preferredSuppliersSet = new Set(
            sourcingSettings?.preferredSuppliers?.map(
                (supplier) => supplier.uid,
            ) || [],
        );

        const hydratedProposals: HydratedProposal[] = linkedProposals.map(
            (proposal) => ({
                ...proposal,
                isPreferred: preferredSuppliersSet.has(proposal.seller.uid),
            }),
        );

        const preferredProposals = hydratedProposals.filter(
            (proposal) => proposal.isPreferred,
        );
        const otherProposals = hydratedProposals.filter(
            (proposal) => !proposal.isPreferred,
        );

        let sortedPreferredProposals = preferredProposals;

        if (sortingMode === ProductPriceSortingModes.BestPrice) {
            // Sort by name for best price mode
            sortedPreferredProposals = sortByPrice(preferredProposals);
        } else {
            // Sort by the order they appear in preferredSuppliers
            const preferredSuppliersOrder =
                sourcingSettings?.preferredSuppliers?.map(
                    (supplier) => supplier.uid,
                ) || [];

            sortedPreferredProposals.sort((a, b) => {
                return (
                    preferredSuppliersOrder.indexOf(a.seller.uid) -
                    preferredSuppliersOrder.indexOf(b.seller.uid)
                );
            });
        }

        return [...sortedPreferredProposals, ...sortByPrice(otherProposals)];
    }, [
        proposals,
        vendorsMap,
        selectedProposals,
        selectedOrderProperty,
        selectedPropertyId,
        sourcingSettings,
        isPreSubmission,
    ]);
};
